// Agent failure alerts for the sidebar AgentBadge.
//
// A failed AgentJob persists in the DB indefinitely, so the badge can't simply
// read "are there failed tasks?" — that would light up forever. Instead we keep
// a client-side, localStorage-backed set of failed task ids the user has
// already acknowledged (by opening the agent panel to check them). The badge
// alerts only on UNSEEN failures: a brand-new failure re-alerts; re-viewing an
// already-seen one keeps the badge idle. Acknowledging stamps every currently
// surfaced failure into the seen set.
//
// The decision logic lives in the pure `unseenFailedCount` / `mergeSeen`
// helpers (unit-tested directly — the web test env is node-only); the hook is a
// thin localStorage + useState wrapper around them.

import { useCallback, useState } from 'react';

const STORAGE_KEY = 'septima.agent.seenFailedIds';

// Cap the persisted set so it can't grow unbounded across a long-lived
// session. Failures are rare, but trimming to the most recent N keeps
// localStorage tidy without ever re-alerting a still-present failure (the
// trim only drops the oldest acknowledged ids).
const SEEN_CAP = 200;

/** Count failed task ids the user has not yet acknowledged. Pure. */
export function unseenFailedCount(failedIds: readonly string[], seen: ReadonlySet<string>): number {
    return failedIds.reduce((n, id) => (seen.has(id) ? n : n + 1), 0);
}

/**
 * Merge the currently surfaced failed ids into the seen set, capped to the most
 * recent `SEEN_CAP` entries (insertion order, newest last). Pure.
 */
export function mergeSeen(
    seen: ReadonlySet<string>,
    failedIds: readonly string[],
    cap: number = SEEN_CAP,
): Set<string> {
    const next = new Set(seen);
    for (const id of failedIds) next.add(id);
    if (next.size <= cap) return next;
    // Drop the oldest entries (Set preserves insertion order).
    return new Set(Array.from(next).slice(next.size - cap));
}

function loadSeen(): Set<string> {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return new Set();
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed)
            ? new Set(parsed.filter((x): x is string => typeof x === 'string'))
            : new Set();
    } catch {
        return new Set();
    }
}

function persistSeen(seen: ReadonlySet<string>): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(seen)));
    } catch {
        // Private mode / quota — alerting degrades to per-session, which is fine.
    }
}

export interface AgentAlerts {
    /** Failed tasks the user has not yet opened the panel to acknowledge. */
    unseenFailedCount: number;
    /** Mark every currently surfaced failure as seen (call when opening the panel). */
    acknowledge: () => void;
}

/**
 * Track unacknowledged agent failures for one badge. `failedIds` is the set of
 * currently-failed task ids the badge surfaces (already scoped to its frontend
 * by the caller); identity should be stable across renders (memoise it).
 */
export function useAgentAlerts(failedIds: readonly string[]): AgentAlerts {
    const [seen, setSeen] = useState<Set<string>>(loadSeen);

    const acknowledge = useCallback(() => {
        setSeen((prev) => {
            const next = mergeSeen(prev, failedIds);
            persistSeen(next);
            return next;
        });
    }, [failedIds]);

    return { unseenFailedCount: unseenFailedCount(failedIds, seen), acknowledge };
}
